<template>
    <v-dialog v-model="show_dialog" max-width="1100" persistent scrollable>
        <v-card>
            <v-card-title class="headline primary white--text">
                <v-icon left color="white">mdi-format-letter-case</v-icon>
                Gentle Name Capitalization
            </v-card-title>

            <v-card-text class="pt-4" style="max-height: 70vh;">
                <!-- Loading State -->
                <div v-if="loading" class="text-center pa-6">
                    <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                    <div class="mt-3 text-body-1">Loading preview...</div>
                </div>

                <!-- Content when loaded -->
                <div v-else>
                    <!-- Rules & Disclaimer Combined -->
                    <v-alert type="info" outlined class="mb-4" :icon="false">
                        <div class="text-subtitle-1 font-weight-medium mb-3">
                            <v-icon left color="info" small>mdi-information</v-icon>
                            How It Works
                        </div>
                        
                        <div class="text-body-2 mb-3">
                            RedChirp's name gentle capitalization feature tries to improve the capitalization of 
                            names for consistency. It is only applied to names that are all lower case or all 
                            upper case. This is a limited, best-effort correction and may not always reflect 
                            the preferred spelling or style of individual names. Please review all imported 
                            names carefully to ensure accuracy before sending messages.
                        </div>
                        
                        <div class="text-body-2">
                            <strong>Rules:</strong>
                            <ul class="pl-4 mt-1 mb-0">
                                <li>Mixed case names (e.g., McFarlane, O'Brien) are not changed</li>
                                <li>Initials are preserved (e.g., TJ, T.J., AJ, DJ)</li>
                                <li>All uppercase/lowercase → Title Case (e.g., JIM → Jim)</li>
                                <li>"and" and "or" stay lowercase (e.g., DAN AND CHRISTIE → Dan and Christie)</li>
                            </ul>
                        </div>
                    </v-alert>

                    <!-- Empty State -->
                    <v-alert v-if="preview_rows.length === 0" type="success" class="mb-0">
                        No names in the current import need capitalization changes. All names already appear to be aligned with our gentle capitalization rules.
                    </v-alert>

                    <!-- Search and Preview Table -->
                    <div v-if="preview_rows.length > 0">
                        <v-text-field
                            v-model="search"
                            prepend-inner-icon="mdi-magnify"
                            label="Search names..."
                            single-line
                            hide-details
                            clearable
                            outlined
                            dense
                            class="mb-3"
                        ></v-text-field>
                        
                        <v-data-table
                            :headers="table_headers"
                            :items="preview_rows"
                            :items-per-page="10"
                            :search="search"
                            dense
                            fixed-header
                            height="400px"
                            class="elevation-1"
                        >
                        <!-- Original First Name Column with change highlighting -->
                        <template v-slot:item.original_first_name="{ item }">
                            <span :class="{ 'text-decoration-line-through red--text text--lighten-1': item.first_name_changed }">
                                {{ item.original_first_name || '(empty)' }}
                            </span>
                        </template>

                        <!-- New First Name Column with change highlighting -->
                        <template v-slot:item.new_first_name="{ item }">
                            <span :class="{ 'font-weight-bold green--text text--darken-2': item.first_name_changed }">
                                {{ item.new_first_name || '(empty)' }}
                            </span>
                        </template>

                        <!-- Original Last Name Column with change highlighting -->
                        <template v-slot:item.original_last_name="{ item }">
                            <span :class="{ 'text-decoration-line-through red--text text--lighten-1': item.last_name_changed }">
                                {{ item.original_last_name || '(empty)' }}
                            </span>
                        </template>

                        <!-- New Last Name Column with change highlighting -->
                        <template v-slot:item.new_last_name="{ item }">
                            <span :class="{ 'font-weight-bold green--text text--darken-2': item.last_name_changed }">
                                {{ item.new_last_name || '(empty)' }}
                            </span>
                        </template>
                        </v-data-table>

                        <!-- Summary -->
                        <div class="mt-3 text-body-2 grey--text text--darken-1">
                            <v-icon small>mdi-information-outline</v-icon>
                            {{ preview_rows.length }} name(s) will be updated
                        </div>
                    </div>
                </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="shaded-light pa-4">
                <v-spacer></v-spacer>
                <!-- When no changes are available, show single Ok button -->
                <v-btn 
                    v-if="!loading && preview_rows.length === 0"
                    color="primary" 
                    @click="closeDialog"
                >
                    Ok
                </v-btn>
                <!-- Normal Cancel/Apply buttons when there are changes -->
                <template v-else>
                    <v-btn text @click="closeDialog" :disabled="applying">
                        Cancel
                    </v-btn>
                    <v-btn 
                        color="primary" 
                        @click="applyChanges" 
                        :disabled="loading"
                        :loading="applying"
                    >
                        <v-icon left>mdi-check</v-icon>
                        Apply Changes
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/**
 * Dialog component for gentle name capitalization feature.
 * Shows a preview of names that will be changed and allows the user to apply the changes.
 */
export default {
    name: 'GentleNameCapitalizationDialog',

    props: {
        organizationId: {
            type: String,
            required: true
        },
        importId: {
            type: String,
            default: ''
        },
        importType: {
            type: String,
            required: true,
            default: 'contact'
        }
    },

    data() {
        return {
            show_dialog: false,
            loading: false,
            applying: false,
            preview_rows: [],
            search: '',
            table_headers: [
                { text: 'Row', value: 'row_number', width: '60px' },
                { text: 'Original First Name', value: 'original_first_name' },
                { text: 'New First Name', value: 'new_first_name' },
                { text: 'Original Last Name', value: 'original_last_name' },
                { text: 'New Last Name', value: 'new_last_name' }
            ]
        };
    },

    methods: {
        /**
         * Open the dialog and load the preview data
         */
        open() {
            if (!this.importId) {
                this.$rc.alert('Error', 'Import ID is missing.');
                return;
            }
            this.show_dialog = true;
            this.loadPreview();
        },

        /**
         * Close the dialog and reset state
         */
        closeDialog() {
            this.show_dialog = false;
            this.preview_rows = [];
        },

        /**
         * Load the preview data from the API
         */
        loadPreview() {
            this.loading = true;
            this.preview_rows = [];

            this.$rc.rest({
                'spinner': false,
                'endpoint': `LIST: /api/v1/imports/${this.importType}/gentle-capitalize/preview/${this.organizationId}/${this.importId}`,
                'params': {},
                'on_success': (data) => {
                    this.preview_rows = data.preview_rows || [];
                    this.loading = false;
                },
                'on_error': (error) => {
                    this.loading = false;
                    this.$rc.alert('Error loading preview', error);
                    this.closeDialog();
                }
            });
        },

        /**
         * Apply the gentle name capitalization changes
         */
        applyChanges() {
            this.applying = true;

            this.$rc.rest({
                'spinner': false,
                'endpoint': `PUT: /api/v1/imports/${this.importType}/gentle-capitalize/apply/${this.organizationId}/${this.importId}`,
                'params': {},
                'on_success': (data) => {
                    this.applying = false;
                    this.$rc.alert('Success', data.message || 'Name capitalization applied successfully');
                    this.$emit('applied', data);
                    this.closeDialog();
                },
                'on_error': (error) => {
                    this.applying = false;
                    this.$rc.alert('Error applying changes', error);
                }
            });
        }
    }
};
</script>

<style scoped>
.text-decoration-line-through {
    text-decoration: line-through;
}
</style>
